import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Mail, Instagram, Send, Upload, X } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import SEO from '@/components/SEO';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Contact = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: '',
    reference_image: ''
  });
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!formData.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email';
    }
    
    if (!formData.message.trim()) {
      newErrors.message = 'Message is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setLoading(true);

    try {
      const response = await axios.post(`${API}/contact`, formData);
      toast.success(response.data.message || 'Thank you for reaching out — we\'ll get back to you shortly.');
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        message: '',
        reference_image: ''
      });
      setErrors({});
    } catch (error) {
      console.error('Failed to send message:', error);
      toast.error('Failed to send message. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = (e) => {
    const file = e.target.files[0];
    if (file) {
      if (file.type === 'image/jpeg' || file.type === 'image/png') {
        // In a real app, you'd upload to a storage service
        // For now, we'll just store the filename
        setFormData({ ...formData, reference_image: file.name });
        toast.success('Image uploaded successfully');
      } else {
        toast.error('Please upload only JPG or PNG files');
      }
    }
  };

  const removeImage = () => {
    setFormData({ ...formData, reference_image: '' });
  };

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#FAFAFA' }}>
      <SEO
        title="Contact"
        description="Get in touch with Navs Art Studio for custom resin commissions, ocean wall art, automotive pieces or general enquiries. Reach us via Instagram @navs_artstudio or email."
        path="/contact"
      />
      <div className="pt-32 pb-24 px-6 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto">
          {/* Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center mb-16"
          >
            <h1
              className="text-4xl sm:text-5xl lg:text-6xl"
              style={{ fontFamily: 'Playfair Display, serif' }}
              data-testid="contact-title"
            >
              Contact Us
            </h1>
          </motion.div>

          {/* Contact Information */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="mb-16"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {/* Email */}
              <a
                href="mailto:navsartstudio@gmail.com"
                data-testid="contact-email"
                className="bg-white p-8 rounded-lg border border-black/5 hover:border-[#1A1A1A] transition-all duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F0] rounded-full flex items-center justify-center group-hover:bg-[#E0E8E9] transition-colors">
                    <Mail size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-widest text-[#888888] mb-1">Email</p>
                    <p className="font-medium">navsartstudio@gmail.com</p>
                  </div>
                </div>
              </a>

              {/* Instagram */}
              <a
                href="https://www.instagram.com/navs_artstudio"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="contact-instagram"
                className="bg-white p-8 rounded-lg border border-black/5 hover:border-[#1A1A1A] transition-all duration-300 group"
              >
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 bg-[#F5F5F0] rounded-full flex items-center justify-center group-hover:bg-[#E0E8E9] transition-colors">
                    <Instagram size={20} strokeWidth={1.5} />
                  </div>
                  <div>
                    <p className="text-sm uppercase tracking-widest text-[#888888] mb-1">Instagram</p>
                    <p className="font-medium">@navs_artstudio</p>
                  </div>
                </div>
              </a>
            </div>
          </motion.div>

          {/* Contact Form */}
          <motion.form
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            onSubmit={handleSubmit}
            className="bg-white p-8 md:p-12 rounded-lg border border-black/5"
            data-testid="contact-form"
          >
            {/* Name Field */}
            <div className="mb-6">
              <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="name">
                Name *
              </label>
              <input
                id="name"
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                data-testid="contact-name-input"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors ${
                  errors.name ? 'border-red-500' : 'border-[#E5E5E5] focus:border-[#1A1A1A]'
                }`}
              />
              {errors.name && (
                <p className="text-red-500 text-sm mt-2">{errors.name}</p>
              )}
            </div>

            {/* Email Field */}
            <div className="mb-6">
              <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="email">
                Email Address *
              </label>
              <input
                id="email"
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                data-testid="contact-email-input"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors ${
                  errors.email ? 'border-red-500' : 'border-[#E5E5E5] focus:border-[#1A1A1A]'
                }`}
              />
              {errors.email && (
                <p className="text-red-500 text-sm mt-2">{errors.email}</p>
              )}
            </div>

            {/* Message Field */}
            <div className="mb-6">
              <label className="block text-sm uppercase tracking-widest mb-2" htmlFor="message">
                Message *
              </label>
              <textarea
                id="message"
                value={formData.message}
                onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                data-testid="contact-message-input"
                rows={6}
                placeholder="Tell us about your idea, inquiry, or custom artwork…"
                className={`w-full px-4 py-3 border rounded-lg focus:outline-none transition-colors resize-none ${
                  errors.message ? 'border-red-500' : 'border-[#E5E5E5] focus:border-[#1A1A1A]'
                }`}
              />
              {errors.message && (
                <p className="text-red-500 text-sm mt-2">{errors.message}</p>
              )}
            </div>

            {/* Reference Image Upload */}
            <div className="mb-8">
              <label className="block text-sm uppercase tracking-widest mb-2">
                Reference Image (Optional)
              </label>
              <div className="border-2 border-dashed border-[#E5E5E5] rounded-lg p-6 hover:border-[#1A1A1A] transition-colors">
                {formData.reference_image ? (
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 bg-[#F5F5F0] rounded-lg flex items-center justify-center">
                        <Upload size={18} strokeWidth={1.5} />
                      </div>
                      <span className="text-sm text-[#4A4A4A]">{formData.reference_image}</span>
                    </div>
                    <button
                      type="button"
                      onClick={removeImage}
                      data-testid="contact-remove-image-btn"
                      className="text-[#888888] hover:text-[#1A1A1A] transition-colors"
                    >
                      <X size={20} />
                    </button>
                  </div>
                ) : (
                  <label htmlFor="file-upload" className="cursor-pointer">
                    <div className="flex flex-col items-center">
                      <Upload size={32} strokeWidth={1.5} className="text-[#888888] mb-3" />
                      <p className="text-sm font-medium mb-1">Click to upload</p>
                      <p className="text-xs text-[#888888]">
                        Upload a reference image (optional — helps us understand your vision)
                      </p>
                      <p className="text-xs text-[#888888] mt-1">JPG or PNG</p>
                    </div>
                    <input
                      id="file-upload"
                      type="file"
                      accept="image/jpeg,image/png"
                      onChange={handleFileUpload}
                      data-testid="contact-image-upload"
                      className="hidden"
                    />
                  </label>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              data-testid="contact-submit-btn"
              className="w-full bg-[#1A1A1A] text-white hover:bg-[#333333] rounded-full px-8 py-6 text-sm uppercase tracking-widest transition-all duration-300 disabled:opacity-50 flex items-center justify-center gap-3"
            >
              {loading ? (
                'Sending...'
              ) : (
                <>
                  <Send size={18} strokeWidth={1.5} />
                  Send Message
                </>
              )}
            </button>
          </motion.form>
        </div>
      </div>
    </div>
  );
};

export default Contact;
